import type {RefObject} from 'react';
import {useEffect} from 'react';

import {useAnnotationModeStore} from '../store/AnnotationModeStore';
import {ANNOTATION_FROZEN_ATTRIBUTE, attachShield, detachShield} from '../shared/annotationFreeze';
import {useIsAnnotationModeAvailable} from '../mode/useIsAnnotationModeAvailable';

interface AnnotationFreezeShieldProps {
  /** The question-area element to freeze (PracticeView's mainWrapperRef). */
  targetRef: RefObject<HTMLDivElement | null>;
}

/**
 * Freezes question interaction while annotation mode is on (#19938). Answer
 * handlers must never use capture-phase document listeners — they'd out-race it.
 */
export default function AnnotationFreezeShield({targetRef}: AnnotationFreezeShieldProps): null {
  const isAnnotationModeAvailable = useIsAnnotationModeAvailable();
  const isAnnotationModeOn = useAnnotationModeStore((state) => state.isAnnotationModeOn);

  useEffect(() => {
    const element = targetRef.current;
    if (!isAnnotationModeOn || !isAnnotationModeAvailable || !element) {
      return undefined;
    }
    element.setAttribute(ANNOTATION_FROZEN_ATTRIBUTE, '');
    attachShield(element);
    return () => {
      element.removeAttribute(ANNOTATION_FROZEN_ATTRIBUTE);
      detachShield(element);
    };
  }, [isAnnotationModeOn, isAnnotationModeAvailable, targetRef]);

  return null;
}
